import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREDIT_TOP_UP_LIMITS } from '@laplace/contracts'
import { useGtmTopUp } from './use-gtm-top-up'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePaymentsConfigQuery } from '@/features/billing/api/billing-queries'
import {
  TOP_UP_PRESETS_USD,
  emailsForUsd,
  parseTopUpAmount,
} from '@/features/billing/domain/credit-balance'
import { PaymentCanceledError } from '@/features/billing/domain/paddle-js'
import { GtmSpinner } from '@/features/gtm/components/gtm-spinner'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { cn } from '@/shared/lib/utils'

/** Any whole amount from $50: presets or the customer's own figure. */
export function GtmTopUpDialog({
  workspaceId,
  open,
  onOpenChange,
}: {
  workspaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const number = new Intl.NumberFormat(lang)
  const paymentsConfig = usePaymentsConfigQuery()
  // Paddle's overlay is open: this dialog steps aside until it closes.
  const [providerCheckoutOpen, setProviderCheckoutOpen] = useState(false)
  const topUp = useGtmTopUp(workspaceId, {
    onProviderCheckout: setProviderCheckoutOpen,
  })
  const [raw, setRaw] = useState(String(CREDIT_TOP_UP_LIMITS.minUsd))
  const [touched, setTouched] = useState(false)
  const amountUsd = parseTopUpAmount(raw)
  const testMode = paymentsConfig.data?.provider === 'fake'
  const canceled = topUp.error instanceof PaymentCanceledError
  const result = topUp.data

  const close = (next: boolean) => {
    if (!next && topUp.isPending) return
    if (!next) topUp.reset()
    onOpenChange(next)
  }

  return (
    <Dialog open={open && !providerCheckoutOpen} onOpenChange={close}>
      <DialogContent className="border-border bg-background text-foreground sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-2xl font-medium tracking-[-0.01em]">
            {t('ws_topUpTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('ws_topUpText', { min: CREDIT_TOP_UP_LIMITS.minUsd })}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="grid gap-4">
            <p role="status" className="text-sm text-foreground">
              {result.confirmed
                ? t('ws_topUpDone', {
                    amount: number.format(result.payment.amountCents / 100),
                  })
                : t('ws_topUpPending')}
            </p>
            <Button type="button" onClick={() => close(false)}>
              {t('checkoutClose')}
            </Button>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              setTouched(true)
              if (amountUsd !== null) topUp.mutate(amountUsd)
            }}
          >
            <div className="flex flex-wrap gap-2">
              {TOP_UP_PRESETS_USD.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-pressed={amountUsd === preset}
                  className={cn(
                    'font-mono tabular-nums',
                    amountUsd === preset && 'border-foreground',
                  )}
                  disabled={topUp.isPending}
                  onClick={() => setRaw(String(preset))}
                >
                  ${number.format(preset)}
                </Button>
              ))}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="gtm-top-up-amount">{t('ws_topUpAmount')}</Label>
              <Input
                id="gtm-top-up-amount"
                inputMode="numeric"
                value={raw}
                disabled={topUp.isPending}
                aria-invalid={touched && amountUsd === null}
                aria-describedby="gtm-top-up-amount-help"
                className="font-mono tabular-nums"
                onBlur={() => setTouched(true)}
                onChange={(event) =>
                  setRaw(event.currentTarget.value.replace(/[^\d]/g, ''))
                }
              />
              <p
                id="gtm-top-up-amount-help"
                className={cn(
                  'text-xs',
                  touched && amountUsd === null
                    ? 'text-destructive'
                    : 'font-mono text-muted-foreground tabular-nums',
                )}
              >
                {touched && amountUsd === null
                  ? t('ws_topUpAmountError', {
                      min: CREDIT_TOP_UP_LIMITS.minUsd,
                      max: number.format(CREDIT_TOP_UP_LIMITS.maxUsd),
                    })
                  : amountUsd !== null
                    ? t('ws_topUpEmails', {
                        emails: number.format(emailsForUsd(amountUsd)),
                      })
                    : null}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {testMode ? t('checkoutTestNote') : t('checkoutPayNote')}
            </p>
            {canceled ? (
              <p role="status" className="text-sm text-muted-foreground">
                {t('checkoutCanceled')}
              </p>
            ) : topUp.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {t('ws_topUpError')}
              </p>
            ) : null}
            <Button
              type="submit"
              className="h-11"
              disabled={topUp.isPending || !topUp.organizationId}
            >
              {topUp.isPending ? <GtmSpinner className="size-4" /> : null}
              {testMode
                ? t('ws_topUpPayTest', {
                    amount: number.format(amountUsd ?? 0),
                  })
                : t('ws_topUpPay', { amount: number.format(amountUsd ?? 0) })}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
